import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ExternalSupport — "Apply support to this group".
 *
 * Acknowledgment, not fundraising. Per directive:
 * - optional link (any URL, no validation)
 * - optional supporter name (blank/"Anonymous" → "Supported by a local donor")
 * - single action, no confirmation screens beyond success toast
 */
export default function ExternalSupport() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const groupId = params.get('group_id');
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [needConfirm, setNeedConfirm] = useState(null);

  useEffect(() => {
    if (!groupId) return;
    axios
      .get(`${API}/api/groups/${groupId}/profile`, { withCredentials: true })
      .then((r) => {
        setGroupName(r.data?.name || '');
        setGroupDesc(r.data?.description || '');
      })
      .catch(() => { /* silent */ });
  }, [groupId]);

  const apply = async (confirmReplace = false) => {
    if (submitting || !groupId) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/support/external`,
        {
          group_id: groupId,
          name: name || null,
          link: link || null,
          confirm_replace: confirmReplace,
        },
        { withCredentials: true },
      );
      toast.success('Support applied.');
      navigate(`/group/${groupId}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof detail === 'object' && detail?.error === 'sponsor_supported') {
        setNeedConfirm(detail.message);
      } else {
        const msg = typeof detail === 'string' ? detail : 'Could not apply.';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="external-support-page">
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-xl mx-auto px-4 flex items-center h-14 gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back" data-testid="external-support-back">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
          </button>
          <h1 className="text-base font-semibold truncate flex-1" style={{ color: 'var(--fy-text-primary)' }}>
            Apply support to this group
          </h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-6 space-y-5">
        {/* Context */}
        <div>
          <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--fy-text-disabled)' }}>
            For
          </p>
          <p className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
            {groupName || 'This group'}
          </p>
          {groupDesc && (
            <p className="text-xs mt-1" style={{ color: 'var(--fy-text-secondary)' }}>
              {groupDesc}
            </p>
          )}
        </div>

        {/* Supporter name (optional) */}
        <div>
          <label className="text-xs font-semibold" htmlFor="es-name" style={{ color: 'var(--fy-text-secondary)' }}>
            Supporter name (optional)
          </label>
          <input
            id="es-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="Anonymous"
            maxLength={80}
            className="mt-1 w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="external-support-name"
          />
          <p className="text-[11px] mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
            Leave blank for a local donor.
          </p>
        </div>

        {/* Support link (optional) */}
        <div>
          <label className="text-xs font-semibold" htmlFor="es-link" style={{ color: 'var(--fy-text-secondary)' }}>
            Support link (optional)
          </label>
          <input
            id="es-link"
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value.slice(0, 500))}
            placeholder="https://"
            maxLength={500}
            className="mt-1 w-full px-3 py-2.5 rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="external-support-link"
          />
        </div>

        {/* Replace-prompt for sponsor-supported groups */}
        {needConfirm && (
          <div
            className="rounded-xl p-3"
            style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
            data-testid="external-support-confirm"
          >
            <p className="text-sm" style={{ color: 'var(--fy-text-primary)' }}>{needConfirm}</p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => setNeedConfirm(null)}
                className="flex-1 px-4 py-2 rounded-full text-sm font-semibold"
                style={{ backgroundColor: 'var(--fy-surface-card)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { setNeedConfirm(null); apply(true); }}
                className="flex-1 px-4 py-2 rounded-full text-sm font-bold"
                style={{ backgroundColor: '#8B6A4F', color: '#FFFFFF' }}
                data-testid="external-support-confirm-yes"
              >
                Replace
              </button>
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => apply(false)}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold transition-transform active:scale-[0.99] disabled:opacity-60"
          style={{ backgroundColor: '#8B6A4F', color: '#FFFFFF' }}
          data-testid="external-support-apply"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Apply support
        </button>
      </main>
    </div>
  );
}
